import { useCallback, useEffect, useRef, useState } from "react"

import {
  Button,
  ChoiceCard,
  ChoiceCardGroup,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SofiaMark,
} from "@coval/ui"

import {
  notebookHistory,
  restoreNotebook,
  type NotebookRevisionDetail,
} from "../utils/notebookHistory"
import type { Notebook } from "../utils/notebookSnapshot"
import { NotebookCanvas } from "./NotebookCanvas"

export function NotebookHistoryDialog({
  open,
  onClose,
  notebook,
  organizationSlug,
  workspaceId,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  onSaved: (notebook: Notebook) => void
}) {
  const [versions, setVersions] = useState<NotebookRevisionDetail[]>([])
  const [preview, setPreview] = useState<NotebookRevisionDetail | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [restoring, setRestoring] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [reload, setReload] = useState(0)
  const request = useRef({ generation: 0 })
  const loadPreview = useCallback(
    async (id: string, generation: number) => {
      setSelected(id)
      setLoadingPreview(true)
      setError("")
      try {
        const data = await notebookHistory(
          organizationSlug,
          workspaceId,
          notebook.id,
          id,
        )
        if (!data.versions[0]) {
          throw new Error("Version is unavailable")
        }
        if (request.current.generation === generation) {
          setPreview(data.versions[0])
        }
      } catch {
        if (request.current.generation === generation) {
          setError("Could not load this version.")
        }
      } finally {
        if (request.current.generation === generation) {
          setLoadingPreview(false)
        }
      }
    },
    [organizationSlug, workspaceId, notebook.id],
  )
  useEffect(() => {
    if (!open) {
      return
    }
    const currentRequest = request.current
    const generation = ++currentRequest.generation
    setPreview(null)
    setSelected(null)
    setError("")
    setVersions([])
    setLoading(true)
    setLoadingPreview(false)
    setRestoring(false)
    void notebookHistory(organizationSlug, workspaceId, notebook.id)
      .then((data) => {
        if (request.current.generation !== generation) {
          return
        }
        setVersions(data.versions)
        if (data.versions[0]) {
          void loadPreview(data.versions[0].revision_id, generation)
        }
      })
      .catch(() => {
        if (request.current.generation === generation) {
          setError("Could not load recent versions.")
        }
      })
      .finally(() => {
        if (request.current.generation === generation) {
          setLoading(false)
        }
      })
    return () => {
      currentRequest.generation++
    }
  }, [open, organizationSlug, workspaceId, notebook.id, loadPreview, reload])
  const restore = async () => {
    if (
      !preview ||
      restoring ||
      loadingPreview ||
      preview.revision_id !== selected
    ) {
      return
    }
    const generation = ++request.current.generation
    setRestoring(true)
    setError("")
    try {
      const saved = await restoreNotebook(
        organizationSlug,
        workspaceId,
        notebook,
        preview.revision_id,
      )
      if (request.current.generation !== generation) {
        return
      }
      onSaved(saved)
      onClose()
    } catch (failure) {
      if (request.current.generation === generation) {
        setError(
          failure instanceof Error ? failure.message : "Could not restore.",
        )
      }
    } finally {
      if (request.current.generation === generation) {
        setRestoring(false)
      }
    }
  }
  const previewReady = preview?.revision_id === selected && !!preview
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !restoring) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-6xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[75vh] overflow-auto">
          <p className="mb-4 text-sm text-coval-text-muted">
            Recent activity · Up to 50 saved versions. Restoring adds a new
            version and keeps your chat.
          </p>
          {error && (
            <div
              role="alert"
              className="mb-3 flex items-center gap-3 text-coval-danger-text"
            >
              <span>{error}</span>
              <Button
                variant="secondary"
                onClick={() =>
                  selected
                    ? void loadPreview(selected, ++request.current.generation)
                    : setReload((value) => value + 1)
                }
              >
                Retry
              </Button>
            </div>
          )}
          <div className="grid gap-4 md:h-[62vh] md:grid-cols-[240px_1fr]">
            <div className="space-y-2 overflow-auto">
              {!loading && !error && !versions.length && (
                <p>No saved changes yet.</p>
              )}
              <ChoiceCardGroup
                aria-label="Saved versions"
                className="space-y-2"
              >
                {versions.map((version) => (
                  <ChoiceCard
                    key={version.revision_id}
                    title={
                      version.source === "sofia"
                        ? "Sofia"
                        : version.source === "original"
                          ? "Original"
                          : version.source === "restore"
                            ? "Restored"
                            : "You"
                    }
                    description={version.summary}
                    selected={selected === version.revision_id}
                    disabled={restoring}
                    onSelect={() =>
                      void loadPreview(
                        version.revision_id,
                        ++request.current.generation,
                      )
                    }
                  >
                    <time className="text-xs text-coval-text-muted">
                      {new Date(version.created_at).toLocaleString()}
                    </time>
                  </ChoiceCard>
                ))}
              </ChoiceCardGroup>
            </div>
            <div className="min-w-0 overflow-auto">
              {loading || loadingPreview ? (
                <div
                  role="status"
                  className="flex min-h-48 items-center justify-center gap-3 text-sm text-coval-text-muted"
                >
                  <SofiaMark size={28} animated />
                  <span>
                    {loading
                      ? "Loading recent activity…"
                      : "Loading version preview…"}
                  </span>
                </div>
              ) : previewReady || (!versions.length && !error) ? (
                <>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-sm text-coval-text-muted">
                      {previewReady ? preview.summary : "Current version"}
                    </p>
                    {previewReady && (
                      <Button
                        disabled={
                          restoring ||
                          !!notebook.locked ||
                          preview.revision_id === notebook.revision_id
                        }
                        onClick={() => void restore()}
                      >
                        {restoring ? "Restoring…" : "Restore this version"}
                      </Button>
                    )}
                  </div>
                  <NotebookCanvas
                    headingOffset={2}
                    notebook={
                      previewReady
                        ? {
                            ...notebook,
                            title: preview.title,
                            document: preview.document,
                            assets: preview.assets,
                          }
                        : notebook
                    }
                    organizationSlug={organizationSlug}
                    workspaceId={workspaceId}
                  />
                </>
              ) : null}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" disabled={restoring} onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
